import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UserProfile } from '../types';
import { callGetControlCentreAvailability } from '../services/functions';
import {
  backendCheckFailedState,
  buildDisabledState,
  fromBackendGate,
  isControlCentreBuildEnabled,
  unauthenticatedState,
  type ControlCentreFrontendGateState,
} from '../featureFlags/controlCentre';

interface UseControlCentreGateResult {
  gate: ControlCentreFrontendGateState;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useControlCentreGate(
  userId: string | null,
  profile: UserProfile | null
): UseControlCentreGateResult {
  const buildEnabled = isControlCentreBuildEnabled();
  const [loading, setLoading] = useState(buildEnabled && Boolean(userId));
  const [gate, setGate] = useState<ControlCentreFrontendGateState>(() => {
    if (!buildEnabled) return buildDisabledState(profile);
    if (!userId) return unauthenticatedState();
    return backendCheckFailedState(profile);
  });

  const seedState = useMemo(() => {
    if (!buildEnabled) return buildDisabledState(profile);
    if (!userId) return unauthenticatedState();
    return null;
  }, [buildEnabled, profile, userId]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!buildEnabled) {
      setGate(buildDisabledState(profile));
      setLoading(false);
      return;
    }

    if (!userId) {
      setGate(unauthenticatedState());
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const backend = await callGetControlCentreAvailability();
      setGate(fromBackendGate(backend));
    } catch {
      setGate(backendCheckFailedState(profile));
    } finally {
      setLoading(false);
    }
  }, [buildEnabled, profile, userId]);

  useEffect(() => {
    if (seedState) {
      setGate(seedState);
      setLoading(false);
      return;
    }
    void refresh();
  }, [refresh, seedState]);

  return { gate, loading, refresh };
}
